import { BLOCKS_MARKER_MIME_TYPE, writeToClipboard } from 'base/clipboard';
import { applyCopySettings } from 'base/copy_settings';
import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { getShadeKey } from 'base/styleguide/concept';
import {
  extractSectionStateMetadata,
  getDefaultScheme,
} from 'base/styleguide/helpers';
import {
  CompiledConcept,
  CompiledScheme,
  ShadeKey,
} from 'base/styleguide/types';
import { TempAsset } from 'base/webflow/asset';
import { toNested } from 'base/webflow/nested';
import { createPageComponent } from 'base/webflow/page_component';
import { textContentsOf } from 'base/webflow/replaceable_content';
import { Component } from 'base/webflow/schema';
import { zodParse } from 'base/zod_parse';
import { exportElementToWebflow } from 'blocks/component_renderer/element_webflow_export';
import { getProjectCopySettings } from 'blocks/copy_settings';
import { exportFormat } from '../export_and_share';
import { exportPageSource, exportSectionSource } from '../export_react_source';
import {
  ElementInstanceState,
  Page,
  PageState,
  Project,
  ProjectState,
  SectionRefState,
  SectionState,
} from '../model';
import WEBFLOW_UPGRADE_COMPONENT from '../webflow_upgrade_component.json';
import { ClipboardPayload, clipboardPayloadSchema } from './types';
export type { ClipboardPayload };

export async function tryReadClipboardApi(
  pushToast: (message: string) => void,
  showNotSupportedDialog: () => void,
): Promise<ClipboardPayload | undefined> {
  if (!navigator.clipboard?.read) {
    showNotSupportedDialog();
    return;
  }
  const items = await navigator.clipboard.read();
  if (!items) {
    pushToast('Your clipboard is empty');
    return;
  }
  const blob = await items
    .find((item) => item.types.includes('text/html'))
    ?.getType('text/html');
  if (!blob) {
    console.warn(
      'Clipboard via api: had no html item',
      items.flatMap((item) => item.types),
    );
    pushToast('That is not a supported paste format');
    return;
  }
  try {
    const decoder = new TextDecoder();
    const text = decoder.decode(await blob.arrayBuffer());
    return parseClipboardHtml(text);
  } catch (e) {
    console.warn('Clipboard parse error', e);
    pushToast('That is not a supported paste format');
  }
}

export async function tryReadClipboardEvent(
  clipboardData: DataTransfer,
  pushToast: (message: string) => void,
): Promise<ClipboardPayload | undefined> {
  if (clipboardData.items.length === 0) {
    pushToast('Your clipboard is empty');
    return;
  }
  const items = Array.from(clipboardData.items);
  const item = items.find((item) => item.type === 'text/html');
  if (!item) {
    console.warn(
      'Clipboard via event: had no html item',
      items.map((item) => item.type),
    );
    pushToast('That is not a supported paste format');
    return;
  }
  try {
    const text = await new Promise<string>((resolve) =>
      item.getAsString(resolve),
    );
    return parseClipboardHtml(text);
  } catch (e) {
    console.warn('Clipboard parse error', e);
    pushToast('That is not a supported paste format');
  }
}

function parseClipboardHtml(html: string): ClipboardPayload {
  const dom = new DOMParser().parseFromString(html, 'text/html');
  const el = dom.querySelector(`[${HTML_DATA_ATTRIBUTE}]`);
  if (!el) {
    throw new Error(`No ${HTML_DATA_ATTRIBUTE} attribute found`);
  }
  const value = el.getAttribute(HTML_DATA_ATTRIBUTE)!;
  return zodParse(clipboardPayloadSchema, JSON.parse(value));
}

export async function copyPage(
  project: Project,
  page: Page,
  library: LibraryCache,
  canExportComponents: boolean,
) {
  const projectState = project.state;
  const pageState = page.state;
  const webflow = canExportComponents
    ? maybeApplyCopySettings(
        exportAllSectionsToWebflow(
          projectState,
          // Do not copy subpages along with the page.
          { ...pageState, subPages: [] },
          library,
        ),
        projectState,
      )
    : WEBFLOW_UPGRADE_COMPONENT;

  const globalIds = page.sections.flatMap((s) =>
    s.type === 'reference' ? [s.id] : [],
  );
  const globalSections = Object.fromEntries<SectionState>(
    globalIds
      .map((id) => {
        const sectionState = projectState.globalSections.get(id);
        return sectionState != null ? ([id, sectionState] as const) : null;
      })
      .filter(exists),
  );
  const text =
    exportFormat.value === 'react'
      ? ''
      : exportFormat.value === 'html'
      ? (await exportPageSource('html', page, library, project)).files[0]
          ?.input ?? ''
      : undefined;

  writeToClipboard(
    encodeClipboardMimes(
      { type: 'page', state: pageState, globalSections },
      webflow,
      text,
    ),
  );
}

const HTML_DATA_ATTRIBUTE = 'data-blocks-payload-v1';

function encodeClipboardMimes(
  payload: ClipboardPayload,
  extraJson: object | undefined,
  text = 'Paste into Webflow or the Relume Figma Plugin to add Wireframes',
) {
  const html = document.createElement('p');
  html.setAttribute(HTML_DATA_ATTRIBUTE, JSON.stringify(payload));
  html.innerText = text;
  return {
    // We add blocksPayloadV1 for the figma extension.
    // We should update the figma extension to read the html instead.
    'application/json': JSON.stringify({
      blocksPayloadV1: payload,
      ...extraJson,
    }),
    'text/html': html.outerHTML,
    'text/plain': text,
    [BLOCKS_MARKER_MIME_TYPE]: '1',
  };
}

export function exportAllSectionsToWebflow(
  project: ProjectState,
  page: PageState,
  library: LibraryCache,
  opts: {
    concept?: CompiledConcept;
    assetByImageKey?: Map<string, TempAsset>;
  } = {},
  schemeShadeKeys: ReadonlyArray<ShadeKey> = [],
) {
  const defaultScheme = getDefaultScheme({
    schemeShadeKeys,
    schemes: opts.concept?.schemes ?? [],
  });

  const preProcessedSections = extractSectionStateMetadata(library, {
    sections: page.sections,
    project,
    getScheme: (elementId) =>
      opts.concept?.schemes.find(
        (scheme) =>
          getShadeKey(scheme) ===
          opts.concept?.state.schemeOverrideByElementId.get(elementId),
      ) ?? defaultScheme,
    concept: opts.concept,
    uiStyle: opts.concept?.state.uiStyle,
    schemelessNavbarSchemeOverride: defaultScheme,
  });

  const sections = preProcessedSections
    .map(({ scheme, section }) => {
      if (!section?.element.current) {
        return;
      }

      let schemeNumber: number | undefined;
      const schemeKey = scheme.shade !== -1 ? getShadeKey(scheme) : null;
      if (schemeKey != null) {
        const idx = schemeShadeKeys.indexOf(schemeKey);
        if (idx !== -1) {
          schemeNumber = idx + 1;
        }
      }

      const component = exportSectionToWebflow(
        section,
        library,
        opts.concept != null
          ? {
              scheme,
              schemeNumber,
              assetByImageKey: opts.assetByImageKey,
            }
          : {},
      );
      if (!component) return;
      return {
        component,
        name: section.name,
        isNavbar: section.element.current
          ? section.element.current.source.slug.includes('navbar')
          : false,
        isFooter: section.element.current
          ? section.element.current.source.slug.includes('footer')
          : false,
      };
    })
    .filter(exists);
  return createPageComponent(page.name, sections);
}

export async function copySection(
  project: ProjectState,
  sref: SectionRefState,
  library: LibraryCache,
  canExportComponents: boolean,
) {
  const value =
    sref.type === 'inline' ? sref.value : project.globalSections.get(sref.id);
  if (value == null) throw new Error('Global section not found');
  const webflow = canExportComponents
    ? maybeApplyCopySettings(exportSectionToWebflow(value, library), project)
    : WEBFLOW_UPGRADE_COMPONENT;
  const globalSections = sref.type === 'reference' ? { [sref.id]: value } : {};
  let sourceCode: string | undefined;
  if (
    (exportFormat.value === 'react' || exportFormat.value === 'html') &&
    value.element.current != null
  ) {
    if (canExportComponents) {
      sourceCode =
        (await exportSectionSource(
          exportFormat.value,
          value.element.current,
          library,
        )) ?? 'Component source code coming soon.';
    } else {
      if (exportFormat.value === 'react') {
        sourceCode = 'Upgrade your plan to copy React components.';
      } else if (exportFormat.value === 'html') {
        sourceCode = 'Upgrade your plan to copy HTML components.';
      }
    }
  }
  writeToClipboard(
    encodeClipboardMimes(
      { type: 'section-ref', state: sref, globalSections },
      webflow,
      sourceCode,
    ),
  );
}

export function exportSectionToWebflow(
  section: SectionState,
  library: LibraryCache,
  opts: {
    scheme?: CompiledScheme;
    schemeNumber?: number;
    assetByImageKey?: Map<string, TempAsset>;
  } = {},
) {
  const root = section.element.current;
  if (!root) return;
  try {
    return exportElementToWebflow(
      root,
      (slug) => library.getCachedPublicComponentJson(slug)?.value,
      opts,
    );
  } catch (e) {
    console.error('Error exporting to webflow on copy:', e);
    return;
  }
}

export function copyElement(
  project: ProjectState,
  element: ElementInstanceState,
  library: LibraryCache,
  canExportComponents: boolean,
) {
  const webflowAlways = exportElementToWebflow(
    element,
    (slug) => library.getCachedPublicComponentJson(slug)?.value,
  );
  const textContent = toNested(webflowAlways.payload.nodes)
    .map((node) => textContentsOf(node))
    .join(' ')
    .trim();
  writeToClipboard(
    encodeClipboardMimes(
      { type: 'element', state: element },
      canExportComponents
        ? maybeApplyCopySettings(webflowAlways, project)
        : WEBFLOW_UPGRADE_COMPONENT,
      textContent,
    ),
  );
}

function maybeApplyCopySettings(
  component: Component | undefined,
  project: ProjectState,
): Component | undefined {
  if (!component) return;
  return applyCopySettings(getProjectCopySettings(project), component);
}
